import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import { getErrorMessage } from '@/api';
import type { PriceRowParams, SizePriceEntry } from '@/api/admin';
import { useSetSizePrice, useSizePrices } from '@/hooks/useAdminProductPrices';
import { priceInputToNumber } from '@/lib/price-input';
import { PriceCell } from './PriceCell';

interface SizePriceRowProps {
  /** Id of the `<tr>`, referenced by the expander's `aria-controls`. */
  id: string;
  productId: string;
  productName: string;
  /** Number of table columns the row spans. */
  colSpan: number;
  priceRowParams: PriceRowParams;
}

const SKELETON_FIELDS = 4;

/**
 * Expanded second row of a product (R17): one blank-cost field per size,
 * aggregated over the size's sellable variants, plus the link to the
 * detail page for anything per colour or position. The parent mounts it
 * only while the product is expanded, so mounting is what loads the sizes.
 */
export function SizePriceRow({
  id,
  productId,
  productName,
  colSpan,
  priceRowParams,
}: SizePriceRowProps) {
  const {
    data: result,
    isLoading,
    isError,
    error,
    refetch,
  } = useSizePrices(productId);
  const setSizePrice = useSetSizePrice(priceRowParams);

  const commitSize = async (size: SizePriceEntry, value: string | null) => {
    await setSizePrice.mutateAsync({
      productId,
      sizeId: size.sizeId,
      purchasePrice: priceInputToNumber(value),
    });
  };

  let content: ReactNode;
  if (isLoading || (!result && !isError)) {
    content = (
      <div className="flex flex-wrap gap-4" aria-busy="true">
        {Array.from({ length: SKELETON_FIELDS }, (_, index) => (
          <span
            key={index}
            className="block h-8 w-24 animate-pulse rounded-control bg-paper-3"
            aria-hidden="true"
          />
        ))}
        <span className="sr-only">Loading sizes</span>
      </div>
    );
  } else if (isError || !result) {
    content = (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-danger">
          Could not load sizes: {getErrorMessage(error)}
        </p>
        <Button size="sm" variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  } else if (!result.hasSizes || result.sizes.length === 0) {
    content = <p className="text-sm text-mute">This product has no sizes</p>;
  } else {
    content = (
      <ul className="flex flex-wrap gap-x-6 gap-y-3">
        {result.sizes.map((size) => {
          const mixed = size.kind === 'mixed';
          return (
            <li key={size.sizeId} className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink">{size.name}</span>
              <PriceCell
                value={size.value}
                placeholder={mixed ? `${size.min}–${size.max}` : null}
                state={mixed ? 'muted' : 'default'}
                hint={mixed ? 'varies' : undefined}
                hintTitle={
                  mixed
                    ? `Variants of this size range from ${size.min} to ${size.max}`
                    : undefined
                }
                aria-label={`${productName}, size ${size.name} blank cost`}
                onCommit={(value) => commitSize(size, value)}
              />
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <tr id={id} className="bg-paper-3/60">
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xs font-medium text-ink-2">
              Blank cost per size · {productName}
            </h3>
            <Link
              to={`/admin/products/${productId}`}
              className="text-xs font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Manage product
            </Link>
          </div>
          {content}
        </div>
      </td>
    </tr>
  );
}
